import { date, MimeType, Statistic, Task } from '@repo/shared';
import {
  createRootRouteWithContext,
  createRoute,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';

import AppLayout from '@/components/blocks/layout/app-layout';
import Centered from '@/components/blocks/layout/centered';
import type { AuthContextProps } from '@/providers/auth-provider';
import { ClusterDetails } from '@/routes/(app)/(clusters)/cluster-details';
import Clusters from '@/routes/(app)/(clusters)/clusters';
import { clusterFiltersSchema } from '@/routes/(app)/(clusters)/lib/schema';
import DatasetDetails from '@/routes/(app)/(datasets)/dataset-details';
import Datasets from '@/routes/(app)/(datasets)/datasets';
import { Leaderboards } from '@/routes/(app)/(leaderboards)/leaderboards';
import Playground from '@/routes/(app)/(playground)/playground';
import StatisticRoute from '@/routes/(app)/(statistics)/statistic';
import TaskDetails from '@/routes/(app)/(tasks)/task-details';
import Login from '@/routes/(auth)/login';
import Root from '@/routes/root';

import './i18n/config';

import Compare from './routes/(app)/(clusters)/compare';

interface RouterContext {
  auth?: AuthContextProps;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Root,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    if (context.auth?.isAuthenticated) {
      throw redirect({ to: '/app/datasets', search: { isEval: true } });
    } else {
      throw redirect({ to: '/auth/login' });
    }
  },
});

const authRoute = createRoute({
  path: 'auth',
  getParentRoute: () => rootRoute,
  component: () => (
    <Centered>
      <Outlet />
    </Centered>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'login',
  validateSearch: z.object({
    redirect: z.string().optional().catch(''),
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth?.isAuthenticated) {
      throw redirect({ to: search.redirect ?? '/app/datasets' });
    }
  },
  component: Login,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'app',
  beforeLoad: ({ context, location }) => {
    if (!context.auth?.isAuthenticated) {
      throw redirect({
        to: '/auth/login',
        search: {
          redirect: location.href,
        },
      });
    }
  },
  component: () => (
    <AppLayout>
      <Outlet />
    </AppLayout>
  ),
});

const datasetsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'datasets',
  component: Datasets,
  validateSearch: (search) =>
    z
      .object({
        task: z.nativeEnum(Task).optional(),
        mimeTypes: z.array(z.nativeEnum(MimeType)).optional(),
        isEval: z.boolean().optional().default(true),
      })
      .parse(search),
});

const datasetsByIdRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'datasets/$datasetId',
  component: DatasetDetails,
});

const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'tasks',
  beforeLoad: () => {
    throw redirect({
      to: '/app/tasks/$task',
      params: {
        task: Task.TEXT_TRANSLATION,
      },
    });
  },
});

const tasksByTaskRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'tasks/$task',
  params: {
    parse: (params) =>
      z
        .object({
          task: z.nativeEnum(Task),
        })
        .parse(params),
  },
  component: TaskDetails,
});

const clustersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'clusters',
  component: Clusters,
  validateSearch: (search) => clusterFiltersSchema.parse(search),
});

const clusterCompareRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'clusters/compare',
  component: Compare,
  validateSearch: (search) =>
    z
      .object({
        clusterIds: z.array(z.string()),
      })
      .parse(search),
});

const clustersByIdRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'clusters/$clusterId',
  component: ClusterDetails,
});

const playgroundRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'playground',
  beforeLoad: () => {
    throw redirect({
      to: '/app/playground/$task',
      params: { task: Task.TEXT_TRANSLATION },
    });
  },
});

const playgroundTaskRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'playground/$task',
  params: {
    parse: (params) =>
      z
        .object({
          task: z.nativeEnum(Task),
        })
        .parse(params),
  },
  validateSearch: (search) =>
    z
      .object({
        clusterIds: z.array(z.string()).optional(),
      })
      .parse(search),
  component: Playground,
});

const leaderboardsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'leaderboards',
  component: Leaderboards,
  validateSearch: (search) =>
    z
      .object({
        datasetIds: z.array(z.string()).default([]),
        task: z.nativeEnum(Task).optional(),
      })
      .parse(search),
});

const statisticsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'statistics',
  params: {
    parse: (params) =>
      z
        .object({
          statistic: z.nativeEnum(Statistic),
        })
        .parse(params),
  },
  beforeLoad: () => {
    throw redirect({
      to: '/app/statistics/$statistic',
      params: {
        statistic: Statistic.TEST_ENVIRONMENT,
      },
    });
  },
});

const statisticRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'statistics/$statistic',
  component: StatisticRoute,
  validateSearch: (search) =>
    z
      .object({
        task: z.nativeEnum(Task).optional(),
        model: z.string().optional(),
        date: z
          .object({
            from: date.optional(),
            to: date.optional(),
          })
          .optional(),
        createdBy: z.string().optional(),
      })
      .parse(search),
});

export const routes = rootRoute.addChildren([
  indexRoute,
  authRoute.addChildren([loginRoute]),
  appRoute.addChildren([
    datasetsRoute,
    datasetsByIdRoute,
    tasksRoute,
    tasksByTaskRoute,
    clustersRoute,
    clusterCompareRoute,
    clustersByIdRoute,
    playgroundRoute,
    playgroundTaskRoute,
    leaderboardsRoute,
    statisticsRoute,
    statisticRoute,
  ]),
]);
